/**
 * Katie & Laura — Favorites page (owned-only, not wishlist)
 *
 * SOT: Customer metafield diary.favorites
 *   json array of { productId, variantId, handle, title, image, addedAt }
 * Same session key as My collection: kl_collection_favs:{customerId}
 * App Proxy (katie-laura-wishlist Worker):
 *   GET  {proxy}/collection/favorites  → { items, updatedAt? }
 *   POST {proxy}/collection/favorites  { action: 'remove', productId, ... }
 * Soft-fail 404 — optimistic list stays for this session.
 */
(function (global) {
  'use strict';

  var DEFAULT_PROXY = '/apps/kl-wishlist';

  function collApi() {
    return global.KLCollectionPage || {};
  }

  function configFrom(root) {
    return {
      loggedIn: !!(root && root.getAttribute('data-logged-in') === 'true'),
      proxy: proxyPath(root),
      customerId: root ? String(root.getAttribute('data-customer-id') || '') : '',
      favRemove: (root && root.getAttribute('data-fav-remove')) || 'In Favs',
      favRemoved: (root && root.getAttribute('data-fav-removed')) || 'Removed from Favs',
      favRemoveConfirm: (root && root.getAttribute('data-fav-remove-confirm')) || 'Remove from Favorites?',
      error: (root && root.getAttribute('data-error')) || 'Couldn’t save just now — try again in a moment.',
      rootUrl: (root && root.getAttribute('data-root')) || '/'
    };
  }

  function proxyPath(root) {
    var raw = String((root && root.getAttribute('data-proxy')) || DEFAULT_PROXY).trim();
    if (!raw) raw = DEFAULT_PROXY;
    if (raw.charAt(0) !== '/') raw = '/' + raw;
    return raw.replace(/\/+$/, '');
  }

  function normalizeId(id) {
    if (id == null || id === '') return '';
    var match = String(id).match(/(\d+)\s*$/);
    return match ? match[1] : String(id);
  }

  function parseJsonScript(root, selector) {
    var el = root && root.querySelector(selector);
    if (!el) return null;
    var text = el.textContent || el.innerText || '';
    if (!text || !String(text).replace(/^\s+|\s+$/g, '')) return null;
    try {
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  }

  function normalizeFavList(value) {
    var api = collApi();
    if (api.normalizeFavList) return api.normalizeFavList(value);
    if (!value) return [];
    var list = value;
    if (typeof list === 'string') {
      try {
        list = JSON.parse(list);
      } catch (e) {
        return [];
      }
    }
    if (!Array.isArray(list)) return [];
    var seen = {};
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var raw = list[i];
      if (!raw || typeof raw !== 'object') continue;
      var productId = normalizeId(raw.productId || raw.product_id);
      if (!productId || seen[productId]) continue;
      seen[productId] = true;
      out.push({
        productId: productId,
        variantId: normalizeId(raw.variantId || raw.variant_id),
        handle: raw.handle ? String(raw.handle) : '',
        title: raw.title ? String(raw.title) : '',
        image: raw.image ? String(raw.image) : '',
        addedAt: raw.addedAt ? String(raw.addedAt) : new Date().toISOString()
      });
    }
    return out;
  }

  function readSession(customerId) {
    var api = collApi();
    if (api.readSession) return api.readSession('favs', customerId);
    try {
      var raw = global.sessionStorage.getItem('kl_collection_favs:' + (customerId || 'anon'));
      if (raw == null) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function writeSession(customerId, value) {
    var api = collApi();
    if (api.writeSession) {
      api.writeSession('favs', customerId, value);
      return;
    }
    try {
      global.sessionStorage.setItem('kl_collection_favs:' + (customerId || 'anon'), JSON.stringify(value));
    } catch (e) {}
  }

  function proxyGet(proxy) {
    return fetch(proxy + '/collection/favorites', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) {
        var err = new Error('kl-favorites ' + res.status);
        err.status = res.status;
        throw err;
      }
      return res.json().catch(function () {
        return {};
      });
    });
  }

  function proxyPost(proxy, body) {
    return fetch(proxy + '/collection/favorites', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) {
        var err = new Error('kl-favorites ' + res.status);
        err.status = res.status;
        throw err;
      }
      return res.json().catch(function () {
        return {};
      });
    });
  }

  function favsFromResponse(data) {
    var api = collApi();
    if (api.favsFromResponse) return api.favsFromResponse(data);
    if (!data) return null;
    if (Array.isArray(data)) return normalizeFavList(data);
    if (Array.isArray(data.items)) return normalizeFavList(data.items);
    if (Array.isArray(data.favorites)) return normalizeFavList(data.favorites);
    return null;
  }

  function starSvg() {
    return (
      collApi().STAR ||
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 2.6l2.35 6.12 6.55.28-5.12 4.08 1.72 6.32L12 15.95l-5.5 3.45 1.72-6.32-5.12-4.08 6.55-.28L12 2.6z"/></svg>'
    );
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function productUrl(rootUrl, handle) {
    if (!handle) return '';
    var base = rootUrl || '/';
    if (base.charAt(base.length - 1) !== '/') base += '/';
    return base + 'products/' + encodeURIComponent(handle);
  }

  function bindRoot(root) {
    if (!root || root.getAttribute('data-kl-bound') === '1') return;
    root.setAttribute('data-kl-bound', '1');

    var cfg = configFrom(root);
    if (!cfg.loggedIn) return;

    var liquidFavs = normalizeFavList(parseJsonScript(root, '[data-kl-favs-json]'));
    var sessionFavs = readSession(cfg.customerId);
    var favs = sessionFavs != null ? normalizeFavList(sessionFavs) : liquidFavs;

    var grid = root.querySelector('[data-kl-favs-grid]');
    var emptyEl = root.querySelector('[data-kl-favs-empty]');
    var statusEl = root.querySelector('[data-kl-favs-status]');

    function persist() {
      writeSession(cfg.customerId, favs);
    }

    function showStatus(message, isError) {
      if (!statusEl) return;
      statusEl.hidden = !message;
      statusEl.textContent = message || '';
      statusEl.classList.toggle('is-error', !!isError);
      clearTimeout(statusEl._klTimer);
      if (message) {
        statusEl._klTimer = setTimeout(function () {
          statusEl.hidden = true;
        }, 2200);
      }
    }

    function paint() {
      if (!grid) return;
      if (!favs.length) {
        grid.innerHTML = '';
        grid.hidden = true;
        if (emptyEl) emptyEl.hidden = false;
        return;
      }
      if (emptyEl) emptyEl.hidden = true;
      grid.hidden = false;
      var html = '';
      for (var i = 0; i < favs.length; i++) {
        var item = favs[i];
        var href = productUrl(cfg.rootUrl, item.handle);
        var title = item.title || 'Favorite';
        html +=
          '<li class="kl-favs-card" data-kl-fav-card data-product-id="' +
          escapeHtml(item.productId) +
          '" data-variant-id="' +
          escapeHtml(item.variantId) +
          '" data-handle="' +
          escapeHtml(item.handle) +
          '" data-title="' +
          escapeHtml(item.title) +
          '" data-image="' +
          escapeHtml(item.image) +
          '">' +
          '<div class="kl-favs-card__row">' +
          (href
            ? '<a class="kl-favs-card__media" href="' + escapeHtml(href) + '">'
            : '<div class="kl-favs-card__media">') +
          (item.image
            ? '<img src="' + escapeHtml(item.image) + '" alt="" width="128" height="128" loading="lazy">'
            : '<span class="kl-favs-card__ph" aria-hidden="true"></span>') +
          (href ? '</a>' : '</div>') +
          '<div class="kl-favs-card__meta">' +
          (href
            ? '<a class="kl-favs-card__name" href="' + escapeHtml(href) + '">' + escapeHtml(title) + '</a>'
            : '<span class="kl-favs-card__name">' + escapeHtml(title) + '</span>') +
          '</div>' +
          '<button type="button" class="kl-favs-card__fav is-on" data-kl-fav-remove aria-pressed="true" aria-label="' +
          escapeHtml(cfg.favRemove) +
          '">' +
          starSvg() +
          '<span>' +
          escapeHtml(cfg.favRemove) +
          '</span></button>' +
          '</div></li>';
      }
      grid.innerHTML = html;
    }

    function itemFromCard(card) {
      if (!card) return {};
      return {
        productId: card.getAttribute('data-product-id') || '',
        variantId: card.getAttribute('data-variant-id') || '',
        handle: card.getAttribute('data-handle') || '',
        title: card.getAttribute('data-title') || '',
        image: card.getAttribute('data-image') || ''
      };
    }

    function askRemoveConfirm(cb) {
      var message = cfg.favRemoveConfirm || 'Remove from Favorites?';
      var ask = global.klDiaryConfirm;
      if (typeof ask === 'function') {
        ask(message, cb);
        return;
      }
      cb(typeof global.confirm === 'function' ? !!global.confirm(message) : false);
    }

    function removeFav(card) {
      var item = itemFromCard(card);
      if (!item.productId) return;
      favs = favs.filter(function (row) {
        return row.productId !== item.productId;
      });
      persist();
      paint();
      showStatus(cfg.favRemoved, false);
      proxyPost(cfg.proxy, Object.assign({ action: 'remove' }, item))
        .then(function (data) {
          var next = favsFromResponse(data);
          if (next) {
            favs = next;
            persist();
            paint();
          }
        })
        .catch(function () {});
    }

    root.addEventListener('click', function (event) {
      var btn = event.target && event.target.closest && event.target.closest('[data-kl-fav-remove]');
      if (!btn) return;
      event.preventDefault();
      var card = btn.closest('[data-kl-fav-card]');
      askRemoveConfirm(function (ok) {
        if (ok) removeFav(card);
      });
    });

    persist();
    paint();

    if (!cfg.proxy) return;

    proxyGet(cfg.proxy)
      .then(function (data) {
        var next = favsFromResponse(data);
        if (next) {
          favs = next;
          persist();
          paint();
        }
      })
      .catch(function () {});
  }

  function ready(cb) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', cb);
    else cb();
  }

  ready(function () {
    var root = document.querySelector('[data-kl-favorites-page]');
    if (root) bindRoot(root);
  });

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target && event.target.querySelector && event.target.querySelector('[data-kl-favorites-page]');
    if (root) {
      root.removeAttribute('data-kl-bound');
      bindRoot(root);
    }
  });

  global.KLFavoritesPage = {
    metafield: { namespace: 'diary', key: 'favorites' },
    normalizeFavList: normalizeFavList,
    favsFromResponse: favsFromResponse
  };
})(typeof window !== 'undefined' ? window : this);
